/**
 * AVA_SNAPS - polygon snaps taken on 360 images for AVA Inspect tags.
 *
 * Holds the saved snaps per tag and the drawing session state that the Snaps tab (side panel) and
 * the draw overlay (inside the 360 viewer) share: they live in different component trees. The
 * polygon being drawn stays local to the overlay; it changes on every pointer move.
 */
import Vue from 'vue';
import { fetchSnaps, createSnap, deleteSnap } from './avaSnapsApi';
import { WAND_TOLERANCE } from './snapConstants';

export const AVA_SNAPS = {
  namespaced: true,

  state: {
    snapsByTagId: {},
    loadingTagId: null,
    /** Tool being drawn with (SNAP_TOOLS value), null when not drawing. */
    activeTool: null,
    /** Tag the drawing session saves into. */
    drawTagId: null,
    /** Where the drawing happens: '360' (overlay on the 360 view) or 'flat' (SnapFlatDialog). */
    drawSurface: '360',
    wandTolerance: WAND_TOLERANCE.DEFAULT,
    isSaving: false,
    /** Snap outlined on the 360 view after it was opened from its card. */
    highlightedSnap: null,
    openingSnapId: null,
  },

  getters: {
    snapsForTag: (state) => (tagId) => state.snapsByTagId[tagId] || [],
    isDrawing: (state) => !!state.activeTool,
    isDrawingOn: (state) => (surface) => !!state.activeTool && state.drawSurface === surface,
  },

  mutations: {
    SET_SNAPS(state, { tagId, snaps }) {
      Vue.set(state.snapsByTagId, tagId, snaps);
    },
    ADD_SNAP(state, { tagId, snap }) {
      Vue.set(state.snapsByTagId, tagId, [snap, ...(state.snapsByTagId[tagId] || [])]);
    },
    REMOVE_SNAP(state, { tagId, snapId }) {
      Vue.set(state.snapsByTagId, tagId, (state.snapsByTagId[tagId] || []).filter((s) => s.id !== snapId));
    },
    SET_LOADING_TAG_ID(state, tagId) {
      state.loadingTagId = tagId;
    },
    SET_ACTIVE_TOOL(state, tool) {
      state.activeTool = tool;
    },
    SET_DRAW_TAG_ID(state, tagId) {
      state.drawTagId = tagId;
    },
    SET_DRAW_SURFACE(state, surface) {
      state.drawSurface = surface;
    },
    SET_WAND_TOLERANCE(state, value) {
      state.wandTolerance = value;
    },
    SET_IS_SAVING(state, value) {
      state.isSaving = value;
    },
    SET_HIGHLIGHTED_SNAP(state, snap) {
      state.highlightedSnap = snap;
    },
    SET_OPENING_SNAP_ID(state, snapId) {
      state.openingSnapId = snapId;
    },
  },

  actions: {
    async FETCH_SNAPS({ commit, rootState }, tagId) {
      commit('SET_LOADING_TAG_ID', tagId);
      try {
        const snaps = await fetchSnaps(rootState.assetId, tagId);
        commit('SET_SNAPS', { tagId, snaps });
        return snaps;
      } finally {
        commit('SET_LOADING_TAG_ID', null);
      }
    },

    /** Starts (or switches) the drawing tool. Other 360 click modes are turned off first. */
    START_DRAWING({ commit, rootState }, { tagId, tool, surface = '360' }) {
      if (rootState.isCropBox360Active) commit('SET_CROP_BOX_360_ACTIVE', false, { root: true });
      commit('SET_HIGHLIGHTED_SNAP', null);
      commit('SET_DRAW_TAG_ID', tagId);
      commit('SET_DRAW_SURFACE', surface);
      commit('SET_ACTIVE_TOOL', tool);
    },

    STOP_DRAWING({ commit }) {
      commit('SET_ACTIVE_TOOL', null);
      commit('SET_DRAW_TAG_ID', null);
      commit('SET_DRAW_SURFACE', '360');
    },

    async SAVE_SNAP({ commit, rootState }, { tagId, meta, thumbnail }) {
      commit('SET_IS_SAVING', true);
      try {
        const snap = await createSnap(rootState.assetId, tagId, meta, thumbnail);
        commit('ADD_SNAP', { tagId, snap });
        return snap;
      } finally {
        commit('SET_IS_SAVING', false);
      }
    },

    async DELETE_SNAP({ commit, state, rootState }, { tagId, snapId }) {
      await deleteSnap(rootState.assetId, tagId, snapId);
      commit('REMOVE_SNAP', { tagId, snapId });
      if (state.highlightedSnap?.id === snapId) commit('SET_HIGHLIGHTED_SNAP', null);
    },
  },
};

export default AVA_SNAPS;
